import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { type FieldTypeWithOnlyValues } from '@databases/field-value';
import { logError, logWarning } from '@databases/logs';
import { useIsMounted } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { getService } from '@databases/service-container';
import { currentSnackbarState } from '@databases/snackbar/snackbar';
import {
  type JiraIssueRef,
  type ResolvedJiraIssueRef
} from '@databases/types/confluence';
import { type DatabaseFieldType } from '@databases/types/database';
import { mapAppearanceToStatusCategory } from '@databases/utils/confluence/jira';
import {
  getReferencedJiraIssueRefs,
  getReferencedJiraValues
} from '@databases/utils/database/field-types/jiraIssueDetails';
import { getDatabaseEntryRequired } from '@databases/utils/database/utils';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { selectJiraIssueOptional } from '@databases/utils/redux/jira/selectors';
import { useCallback, useEffect, useState } from 'react';

import { type EditComponentProps } from '../types.ts';

import { getStatusTransitions, updateIssueStatus, type TransitionItem } from './api.ts';

export function useIssueTransitions(
  issueRef: JiraIssueRef | undefined,
  hostname?: string,
  shouldPreload?: boolean
) {
  const [isLoading, setIsLoading] = useState(false);
  const [transitions, setTransitions] = useState<TransitionItem[]>([]);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
  const isMountedRef = useIsMounted();

  /* oxlint-disable react/exhaustive-deps */
  const fetchTransitions = useCallback(async () => {
    if (!issueRef) {
      return [];
    }

    // If we've already attempted to fetch and got empty results, don't fetch again
    if (hasAttemptedFetch && transitions.length === 0) {
      return [];
    }

    setIsLoading(true);
    try {
      const items = await getStatusTransitions({
        issueKey: issueRef.key,
        hostname,
        cloudId: issueRef.cloudId
      });
      if (isMountedRef.current) {
        setTransitions(items);
        setHasAttemptedFetch(true);
        return items;
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
    return [];
    // Note: transitions.length is intentionally omitted from dependencies to prevent
    // unnecessary callback recreation when transitions change, which would defeat the caching mechanism
  }, [hostname, issueRef, isMountedRef, hasAttemptedFetch]); // eslint-disable-line react-hooks/exhaustive-deps
  /* oxlint-enable react/exhaustive-deps */

  useEffect(() => {
    if (shouldPreload && issueRef && !hasAttemptedFetch) {
      fetchTransitions();
    }
  }, [shouldPreload, issueRef, hasAttemptedFetch, fetchTransitions]);

  const clearTransitions = useCallback(() => {
    setTransitions([]);
    setHasAttemptedFetch(false);
  }, []);

  return {
    isLoading,
    transitions,
    fetchTransitions,
    clearTransitions,
    isMountedRef
  } as const;
}

export function useOnSelectTransition(params: {
  issueRef: JiraIssueRef | undefined;
  hostname?: string;
  transitions: TransitionItem[];
  resolvedIssue: ResolvedJiraIssueRef | undefined;
  isMountedRef: ReturnType<typeof useIsMounted>;
  clearTransitions: () => void;
  currentStatusName?: string;
  currentStatusId?: string;
  onOptimisticUpdate?: (optimisticIssue: ResolvedJiraIssueRef) => void;
  onReloadIssue?: (issueRef: JiraIssueRef) => Promise<void>;
  contentId: string;
}) {
  const [isLoading, setIsLoading] = useState(false);

  const {
    issueRef,
    hostname,
    transitions,
    resolvedIssue,
    isMountedRef,
    clearTransitions,
    currentStatusName,
    currentStatusId,
    onOptimisticUpdate,
    onReloadIssue,
    contentId
  } = params;

  const analytics = useAnalytics();

  const selectTransition = useCallback(
    async (transitionId: string) => {
      if (!issueRef) {
        return;
      }

      // Prevent updating to the same status
      // Check both by ID (for current status clicks) and by name (for transition matches)
      if (currentStatusId && transitionId === currentStatusId) {
        return;
      }

      if (currentStatusName) {
        const selectedTransition = transitions.find((t) => t.id === transitionId);
        if (selectedTransition && selectedTransition.name === currentStatusName) {
          return;
        }
      }

      setIsLoading(true);
      try {
        await updateIssueStatus({
          issueKey: issueRef.key,
          hostname,
          cloudId: issueRef.cloudId,
          newStatusId: transitionId
        });
      } catch (e) {
        logError(
          'jira-status/update:failed',
          e instanceof Error ? e : new Error('jira status update failed')
        );
        currentSnackbarState.setMessage('Failed to update status', {
          id: 'jira-status-update-failed',
          type: 'error',
          showCloseButton: true,
          hideAfter: 4000
        });
        return;
      }

      const chosen = transitions.find((t) => t.id === transitionId);

      // Send analytics event for successful status update
      if (chosen && issueRef) {
        analytics.sendJiraFieldUpdatedEvent(
          {
            contentId,
            issueKey: issueRef.key,
            fieldType: 'status',
            databaseContainer: getDatabaseContainerType()
          },
          'jiraStatus',
          'jiraStatusValueEdit'
        );
      }
      if (resolvedIssue && chosen && onOptimisticUpdate) {
        const mappedCategory = mapAppearanceToStatusCategory(chosen.appearance);
        const optimistic = {
          ...resolvedIssue,
          statusName: chosen.name,
          statusCategory: mappedCategory
        } as any;
        onOptimisticUpdate(optimistic);
      }

      try {
        if (isMountedRef.current && onReloadIssue) {
          await onReloadIssue(issueRef);
        }
        if (resolvedIssue?.url) {
          const smartLinkClient = getService('SmartLinkClient');
          smartLinkClient.forceRefreshData(resolvedIssue.url);
        }
      } catch (e) {
        const err = e instanceof Error ? e : new Error('jira status reload failed');
        err.message = `${err.message} | issueKey=${issueRef?.key ?? 'unknown'} hostname=${
          hostname ?? 'unknown'
        }`;
        logWarning('jira-status/reload:failed', err);
        return;
      }
      clearTransitions();
      setIsLoading(false);
    },
    [
      issueRef,
      hostname,
      transitions,
      resolvedIssue,
      isMountedRef,
      clearTransitions,
      currentStatusName,
      currentStatusId,
      onOptimisticUpdate,
      onReloadIssue,
      contentId,
      analytics
    ]
  );

  return { selectTransition, isLoading };
}

/**
 * Combined selector that fetches both issue refs and status items in a single call
 * to avoid duplicate database/entry lookups
 */
export function useIssueRefsAndStatusItems(
  contentId: string,
  entryId: string | undefined,
  field: EditComponentProps<DatabaseFieldType.JIRA_ISSUE_DETAILS>['field']
) {
  return defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, { contentId });
    const entry = entryId ? getDatabaseEntryRequired(database, entryId) : undefined;

    if (!entry) {
      return { issueRefs: [], statusItems: [] };
    }

    // Get issue refs
    const issueRefs = getReferencedJiraIssueRefs({ database, field, entry }, state);

    // Get status items
    const vals = getReferencedJiraValues({ database, field, entry }, state);
    const statusItems = vals.reduce((acc, curr) => {
      if (curr && curr.data) {
        const values = Array.isArray(curr.data) ? curr.data : [curr.data];
        acc.push({
          type: curr.type,
          text: curr.text,
          values
        } as unknown as FieldTypeWithOnlyValues);
      }
      return acc;
    }, [] as FieldTypeWithOnlyValues[]);

    return { issueRefs, statusItems };
  });
}

export function useResolvedIssueAndHost(issueRef: JiraIssueRef | undefined) {
  return defaultStoreUtils.useShallowEqualSelector((state) => {
    const resolved = issueRef ? selectJiraIssueOptional(state, issueRef) : undefined;
    const statusName = resolved?.statusName;
    let host: string | undefined;
    if (resolved?.url) {
      try {
        host = new URL(resolved.url).hostname;
      } catch (e) {
        const err = e instanceof Error ? e : new Error('invalid jira issue url');
        logWarning('jira-status/url-parse:failed', err);
        // Intentionally set host to undefined as fallback for invalid URLs
        host = undefined;
      }
    }
    return [resolved, statusName, host] as const;
  });
}

export function buildTransitionPreviewItem(
  statusItem: FieldTypeWithOnlyValues | undefined,
  t: TransitionItem
): FieldTypeWithOnlyValues {
  if (statusItem && Array.isArray((statusItem as any).values)) {
    const base = (statusItem as any).values[0] ?? {};
    const style = base.style
      ? { ...base.style, appearance: t.appearance ?? base.style.appearance ?? 'default' }
      : { appearance: t.appearance ?? 'default' };
    return {
      ...statusItem,
      values: [
        {
          ...base,
          text: t.name,
          style
        }
      ]
    } as unknown as FieldTypeWithOnlyValues;
  }

  return {
    type: 'status',
    text: undefined,
    values: [{ text: t.name, style: { appearance: t.appearance ?? 'default' } }]
  } as unknown as FieldTypeWithOnlyValues;
}
